<script setup lang="ts">
import { ErrorMessage, Field, Form } from "vee-validate";
import { ref } from "vue";
import { object, string, ref as yupRef } from "yup";
import countries from "@/data/countries.json";
import countryCodes from "@/data/country-codes.json";
import "v-calendar/style.css";
import {
  type EnquiryCreateResponse,
  isDuplicateEnquiryResponse,
} from "@/utils/enquiryCreate";

const emit = defineEmits<{ success: [] }>();

const route = useRoute();
const page = usePage().value;
const dateTime = ref(new Date().toLocaleString());
const title = ref("");
const firstName = ref("");
const lastName = ref("");
const email = ref("");
const confirmEmail = ref("");
const countryCode = ref("");
const phone = ref("");
const country = ref("");
const trekPackage = ref("");
const arrivalMonth = ref("");
const arrivalYear = ref("");
const numPeople = ref("");
const enquiry = ref("");
const hearAbout = ref("");
const hearOther = ref("");
const agreeTerms = ref("");
const marketingConsent = ref<string>(""); // initialize with an empty string
const honeypot = ref(""); // Simple honeypot field for spam filtering

const emailRegex = /^[\w.%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
const phoneRegex = /^[1-9]\d{1,14}$/;
const monthOptions = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 6 }, (_, i) => String(currentYear + i));

const schema = object().shape({
  title: string().required("Please enter title"),
  firstName: string().required("Please enter first name"),
  lastName: string().required("Please enter last name"),
  email: string()
    .matches(emailRegex, "Please enter a valid email address")
    .required("Please enter an email"),
  confirmEmail: string()
    .oneOf([yupRef("email")], "Emails must match")
    .required("Please confirm your email"),
  countryCode: string().required("Please select country code"),

  phone: string()
    .required("Please enter a phone number")
    .matches(phoneRegex, "Phone number must contain digits only"),
  country: string().required("Please select country"),
  trekPackage: string().required("Please select trek package"),
  arrivalMonth: string().required("Please select preferred arrival month"),
  arrivalYear: string().required("Please select preferred arrival year"),
  numPeople: string().required("Please select group size"),
  enquiry: string().required("Please enter your enquiry"),
  //   agreeTerms: string()
  //     .required("Please agree to the terms")
  //     .oneOf(
  //       [
  //         "I agree with Mont Blanc Trek's privacy statement which can be found in the footer of this page",
  //       ],
  //       "Please agree to the terms",
  //     ),
});

const marketingConsentChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target?.checked) {
    marketingConsent.value = "Yes please";
  }
  if (!target?.checked) {
    marketingConsent.value = "No thanks";
  }
};

const handleTrekPackageChange = (event: Event) => {
  const selectedValue = (event.target as HTMLSelectElement).value;
  // Value is now managed by vee-validate, but we can still log it
  console.log("Trek Package Changed:", selectedValue);
};

const sending = ref(false);
const emailSent = ref(false);
const isDuplicateSubmission = ref(false);
const submitError = ref("");

const onSubmit = async (_values: Record<string, unknown>) => {
  if (honeypot.value !== "") return;

  sending.value = true;
  submitError.value = "";

  const arrivalDate =
    arrivalMonth.value && arrivalYear.value
      ? `${arrivalMonth.value} ${arrivalYear.value}`
      : "";

  const payload = {
    first_name: firstName.value.trim(),
    last_name: lastName.value.trim(),
    email: email.value.trim(),
    phone: phone.value.trim() || undefined,
    title: title.value || undefined,
    country: country.value || undefined,
    country_code: countryCode.value || undefined,
    message: enquiry.value.trim() || undefined,
    marketing_consent: marketingConsent.value || undefined,
    source_page: typeof route.path === "string" ? route.path : "/",
    submission_id: crypto.randomUUID(),
    enquiry_type: "enquiry page",
    date_time: new Date().toLocaleString(),
    arrival_date: arrivalDate || undefined,
    num_people: numPeople.value || undefined,
    hear_about: hearAbout.value || undefined,
    hear_other: hearOther.value || undefined,
    trek_package: trekPackage.value || undefined,
    user_agent: navigator.userAgent,
  };

  try {
    const response = await $fetch<EnquiryCreateResponse>("/api/enquiries/create", {
      method: "POST",
      body: payload,
    });
    isDuplicateSubmission.value = isDuplicateEnquiryResponse(response);
    emailSent.value = true;
    if (!isDuplicateSubmission.value) {
      emit("success");
    }
  } catch (err: unknown) {
    const message =
      err &&
      typeof err === "object" &&
      "data" in err &&
      err.data &&
      typeof (err.data as { message?: string }).message === "string"
        ? (err.data as { message: string }).message
        : "Something went wrong. Please try again.";
    submitError.value = message;
  } finally {
    sending.value = false;
  }
};
</script>

<template>
  <ClientOnly>
    <div class="contact-form">
      <Form
        v-if="!emailSent"
        id="myForm"
        :validation-schema="schema"
        @submit="onSubmit"
      >
        <input v-model="dateTime" type="hidden" name="dateTime" />

        <!-- Simple honeypot field - hidden from users -->
        <input
          v-model="honeypot"
          type="text"
          name="website"
          class="honeypot-field"
          autocomplete="off"
          tabindex="-1"
          aria-hidden="true"
        />
        <div class="form-col col-3">
          <div class="form-control">
            <label for="title">Title</label>
            <div class="select-wrapper">
              <Field
                id="title"
                v-model="title"
                as="select"
                name="title"
                class="select-styled"
              >
                <option value="" disabled>Please select...</option>
                <option value="Mr">Mr</option>
                <option value="Mrs">Mrs</option>
                <option value="Ms">Ms</option>
                <option value="Miss">Miss</option>
                <option value="Mx">Mx</option>
              </Field>
            </div>
            <Transition name="error" appear>
              <ErrorMessage class="error" name="title" />
            </Transition>
          </div>
          <div class="form-control">
            <label for="firstName">First Name</label>
            <Field
              id="firstName"
              v-model="firstName"
              type="text"
              label="First name"
              name="firstName"
              placeholder="First name*"
            />
            <Transition name="error" appear>
              <ErrorMessage class="error" name="firstName" />
            </Transition>
          </div>
          <div class="form-control">
            <label for="lastName">Last Name</label>
            <Field
              id="lastName"
              v-model="lastName"
              type="text"
              label="Last name"
              name="lastName"
              placeholder="Last name*"
            />
            <Transition name="error" appear>
              <ErrorMessage class="error" name="lastName" />
            </Transition>
          </div>
        </div>

        <div class="form-col col-2">
          <div class="form-control">
            <label for="email">Email</label>
            <Field
              id="email"
              v-model="email"
              type="email"
              name="email"
              label="Email"
              placeholder="Email*"
            />
            <Transition name="error" appear>
              <ErrorMessage class="error" name="email" />
            </Transition>
          </div>
          <div class="form-control">
            <label for="confirmEmail">Confirm Email</label>
            <Field
              id="confirmEmail"
              v-model.trim="confirmEmail"
              name="confirmEmail"
              type="email"
              placeholder="Re-type Email"
            />
            <Transition name="error" appear>
              <ErrorMessage class="error" name="confirmEmail" />
            </Transition>
          </div>
        </div>
        <div class="form-col col-3">
          <div class="form-control">
            <label for="countryCode">Int. Dialling Code</label>
            <div class="select-wrapper">
              <Field
                id="countryCode"
                v-model="countryCode"
                as="select"
                name="countryCode"
                class="select-styled"
              >
                <option value="" disabled>Please select...</option>
                <option
                  v-for="code in countryCodes"
                  :key="code.value"
                  :value="code.value"
                >
                  {{ code.name }}
                </option>
              </Field>
            </div>
            <Transition name="error" appear>
              <ErrorMessage class="error" name="countryCode" />
            </Transition>
          </div>
          <div class="form-control">
            <label for="phone">Telephone</label>
            <Field
              id="phone"
              v-model="phone"
              type="tel"
              name="phone"
              label="Phone"
              placeholder="Phone number"
            />
            <Transition name="error" appear>
              <ErrorMessage class="error" name="phone" />
            </Transition>
          </div>
          <div class="form-control">
            <label for="country">Country of Residence</label>
            <div class="select-wrapper country">
              <Field
                id="country"
                v-model.trim="country"
                as="select"
                name="country"
                class="select-styled"
              >
                <option value="" disabled>Please select...</option>
                <option
                  v-for="code in countries"
                  :key="code.value"
                  :value="code.value"
                >
                  {{ code.label }}
                </option>
              </Field>
              <Transition name="error" appear>
                <ErrorMessage class="error" name="country" />
              </Transition>
            </div>
          </div>
        </div>
        <div class="form-col col-3">
          <div class="form-control">
            <label for="trekPackage">Trek Package</label>
            <div class="select-wrapper">
              <Field
                id="trekPackage"
                v-model="trekPackage"
                as="select"
                name="trekPackage"
                class="select-styled"
                @change="handleTrekPackageChange"
              >
                <option value="" selected disabled>Please select...</option>
                <option
                  v-for="trek in page?.trekPackagesList"
                  :key="trek.id"
                  :value="`${trek.trek_route} - ${trek.trek_title}`"
                >
                  {{ trek.trek_route }} - {{ trek.trek_title }}
                </option>
              </Field>
              <Transition name="error" appear>
                <ErrorMessage class="error" name="trekPackage" />
              </Transition>
            </div>
          </div>
          <div class="form-control">
            <label for="arrivalMonth">Preferred Arrival Date</label>
            <Transition name="error" appear>
              <ErrorMessage class="error error-inline" name="arrivalMonth" />
            </Transition>
            <div class="form-col col-2">
              <Field
                id="arrivalMonth"
                v-model="arrivalMonth"
                as="select"
                name="arrivalMonth"
                class="select-styled"
                aria-label="Preferred arrival month"
              >
                <option value="" disabled>Please select month...</option>
                <option
                  v-for="month in monthOptions"
                  :key="month"
                  :value="month"
                >
                  {{ month }}
                </option>
              </Field>
              <Field
                id="arrivalYear"
                v-model="arrivalYear"
                as="select"
                name="arrivalYear"
                class="select-styled"
                aria-label="Preferred arrival year"
              >
                <option value="" disabled>Please select year...</option>
                <option v-for="year in yearOptions" :key="year" :value="year">
                  {{ year }}
                </option>
              </Field>
            </div>
            <Transition name="error" appear>
              <ErrorMessage class="error" name="arrivalYear" />
            </Transition>
          </div>
          <div class="form-control">
            <label for="numPeople">Group Size</label>
            <Field
              id="numPeople"
              v-model="numPeople"
              type="number"
              name="numPeople"
              label="numPeople"
              placeholder="Number of people"
            />
            <Transition name="error" appear>
              <ErrorMessage class="error" name="numPeople" />
            </Transition>
          </div>
        </div>
        <div class="form-col col-1">
          <div class="form-control">
            <label for="enquiry">Message</label>
            <Field
              id="enquiry"
              v-model="enquiry"
              as="textarea"
              label="enquiry"
              type="textarea"
              class="textarea"
              name="enquiry"
              placeholder="Message ..."
            />
            <Transition name="error" appear>
              <ErrorMessage class="error" name="enquiry" />
            </Transition>
          </div>
        </div>
        <div class="form-col col-2">
          <div class="form-control">
            <label for="hearAbout">How did you hear about us?</label>
            <div class="select-wrapper">
              <Field
                id="hearAbout"
                v-model="hearAbout"
                as="select"
                name="hearAbout"
                class="select-styled"
              >
                <option value="" selected disabled>Please select...</option>
                <option value="Google Search">Google search</option>
                <option value="Word of Mouth/Recommendation">
                  Word of mouth/recommendation
                </option>
                <option value="Existing client">Existing client</option>
                <option value="Instagram">Instagram</option>
                <option value="Facebook">Facebook</option>
                <option value="Blog">Blog</option>
                <option value="Other">Other</option>
              </Field>
            </div>
          </div>
          <div class="form-control">
            <label for="hearOther">If other please specify</label>
            <Field
              id="hearOther"
              v-model="hearOther"
              type="text"
              label="If other please specify"
              name="hearOther"
              placeholder="If other please specify"
            />
            <Transition name="error" appear>
              <ErrorMessage class="error" name="hearOther" />
            </Transition>
          </div>
        </div>
        <div class="checkbox-wrap">
          <div class="form-col col-2">
            <div class="form-control">
              <AppFormCustomCheckbox
                v-model="agreeTerms"
                name="agreeTerms"
                value="I agree with Mont Blanc Trek's privacy statement which can be found in the footer of this page"
              />

              <Transition name="error" appear>
                <ErrorMessage class="error" name="agreeTerms" />
              </Transition>
            </div>
            <div class="form-control">
              <AppFormCustomCheckbox
                v-model="marketingConsent"
                name="marketingConsent"
                value="Please contact me over email with related information"
                @change="marketingConsentChange($event)"
              />
            </div>
          </div>
        </div>

        <Field
          v-model="marketingConsent"
          type="hidden"
          name="marketingConsent"
        />

        <div v-if="submitError" class="form-error">
          {{ submitError }}
        </div>
        <button class="submit-btn btn" type="submit" :disabled="sending">
          <span v-if="sending">Sending...</span>
          <span v-else>Send</span>
        </button>
      </Form>
      <div v-else>
        <Transition name="slideleft" appear>
          <div
            v-if="emailSent"
            id="formEndScreen"
            :class="{ 'form-end-screen--duplicate': isDuplicateSubmission }"
          >
            <template v-if="isDuplicateSubmission">
              <h3>We already have your enquiry</h3>
              <p class="form-end-screen-note">
                We received your details a few moments ago, so there is no need
                to send the form again. We will be in touch shortly.
              </p>
            </template>
            <template v-else>
              <h3>
                Thanks for your
                <span v-if="country === 'United States'">inquiry!</span>
                <span v-else>enquiry!</span> Your first step on your trekking
                adventure!
              </h3>
            </template>
          </div>
        </Transition>
      </div>
    </div>
  </ClientOnly>
</template>

<style scoped>
/* Simple honeypot field - hidden from users */
.honeypot-field {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}
</style>
